import { Heading, SegmentedTabs } from "@overmap-ai/blocks"
import { type AssetTypeFields, type Stored, EMPTY_ARRAY } from "@overmap-ai/common"
import {
	selectAssetTypeById,
	selectAssetTypeFieldsOfAssetType,
	selectAttachmentsOfAssetTypeFields,
	selectLatestAssetTypeFieldsOfAssetType,
} from "@overmap-ai/core"
import { FieldSchema, type FieldSchemaConfig, FormBuilder, getImagesFromFields } from "@overmap-ai/forms"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { FieldsRevisionsTimeline } from "../../components/FieldsRevisionsTimeline/FieldsRevisionsTimeline"
import { useDeserializeFieldSchemaConfig, useHydrateFieldsWithAttachments } from "../../components/Form/hooks"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/asset-types/$assetTypeId/fields")

export const AssetTypeSettingsFieldsView = memo(() => {
	const { assetTypeId } = routeApi.useParams()
	const assetType = useAppSelector(selectAssetTypeById(assetTypeId))

	if (!assetType) {
		throw new Error(`expected asset type to be defined, got ${assetType}`)
	}

	const latestAssetTypeFields = useAppSelector(selectLatestAssetTypeFieldsOfAssetType(assetType.uuid))
	const assetTypeFieldsRevisions = useShallowEqaulSelector(selectAssetTypeFieldsOfAssetType(assetType.uuid))
	const assetTypeFieldsAttachments = useShallowEqaulSelector(
		selectAttachmentsOfAssetTypeFields(latestAssetTypeFields?.uuid ?? ""),
	)
	const navigate = useNavigate()

	const handleAssetTypeFieldsSelect = useCallback(
		(assetTypeFields: Stored<AssetTypeFields>) => {
			void navigate({
				from: "/$organizationId/settings/asset-types/$assetTypeId/fields",
				to: "/$organizationId/settings/asset-types/$assetTypeId/revisions/$assetTypeFieldsId",
				params: (previousParams) => ({ ...previousParams, assetTypeFieldsId: assetTypeFields.uuid }),
			})
		},
		[navigate],
	)

	const handleFormBuilderSave = useCallback(
		async (fields: FieldSchemaConfig) => {
			const images = await getImagesFromFields(fields)

			const [offlineAssetTypeFields, _promise] = sdk.assetTypeFields.add({
				asset_type: assetType.uuid,
				fields: FieldSchema.serializeConfig(fields),
			})

			if (Object.keys(images).length !== 0) {
				void sdk.assetTypeFieldsAttachments.bulkAdd(
					Object.entries(images).map(([fieldId, file]) => ({
						fields_revision: offlineAssetTypeFields.uuid,
						field_identifier: fieldId,
						file: file,
					})),
				)
			}
		},
		[assetType.uuid],
	)

	const fieldSchemaConfig = useDeserializeFieldSchemaConfig(latestAssetTypeFields?.fields ?? EMPTY_ARRAY)

	const fieldsWithImages = useHydrateFieldsWithAttachments(
		fieldSchemaConfig,
		assetTypeFieldsAttachments,
		sdk.assetTypeFieldsAttachments,
	)

	return (
		<ViewContainer>
			<section className="flex size-full flex-col gap-4">
				<Heading size="xl">Fields</Heading>

				<FormBuilder.Root initialFields={fieldsWithImages} onSave={handleFormBuilderSave}>
					<SegmentedTabs.Root defaultValue="edit" className="flex size-full flex-col gap-4 overflow-hidden">
						<SegmentedTabs.List className="flex" accentColor="base" size="sm" variant="surface">
							<SegmentedTabs.Trigger className="grow basis-1" value="edit">
								List
							</SegmentedTabs.Trigger>
							<SegmentedTabs.Trigger className="grow basis-1" value="flow">
								Flow
							</SegmentedTabs.Trigger>
							<SegmentedTabs.Trigger className="grow basis-1" value="preview">
								Preview
							</SegmentedTabs.Trigger>
							<SegmentedTabs.Trigger className="grow basis-1" value="history">
								History
							</SegmentedTabs.Trigger>
						</SegmentedTabs.List>

						<SegmentedTabs.Content value="edit" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.ListBuilder />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="flow" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.FlowBuilder />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="preview" className="size-full overflow-y-auto overflow-x-clip">
							<FormBuilder.Preview />
						</SegmentedTabs.Content>

						<SegmentedTabs.Content value="history" className="size-full overflow-y-auto overflow-x-clip">
							<FieldsRevisionsTimeline
								fieldsRevisions={assetTypeFieldsRevisions}
								onRevisionSelect={handleAssetTypeFieldsSelect}
							/>
						</SegmentedTabs.Content>
					</SegmentedTabs.Root>
				</FormBuilder.Root>
			</section>
		</ViewContainer>
	)
})
AssetTypeSettingsFieldsView.displayName = "AssetTypeSettingsFieldsView"
